import React, { useEffect, forwardRef, useImperativeHandle, useState } from 'react';
import { useFormik, getIn } from 'formik';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Slider from '@mui/material/Slider';
import Stack from '@mui/material/Stack';

import { formatNumberThsndAndDec, formatNumberMagnitude } from '../helpers/stringFormat';
import { getMagnitude } from './valueFunctions';
import { addCurrencyFrequency } from '../helpers/currencyFormat';
import { Typography } from '@mui/material';
import { GreenRedCompactSwitch, TextFieldCustom } from '../helpers/formFields';
import firstCharToUpper from '../helpers/stringFormat';
import { createSliderMarks, getSliderInitialValues, getMultipleInitialValues } from './formFunctions';
import { tooltip, generalTooltipText } from '../helpers/tooltip';

const frequencyTransfer = {
    daily: "day",
    monthly: "month",
    quarterly: "quarter",
    yearly: "year",
};

const CostValueDriverCSForm = forwardRef(({ currency, outcome, outcomeList, product, costStructure, editCSDriverId, editCSDriver, formActions, onSubmit }, ref) => {
    const [positiveDriver, setPositiveDriver] = useState(true);
    const [openOutcome, setOpenOutcome] = useState(false);

    const tooltipText = {
        shareContacting: `Share of active customers contacting customer service per ${product['frequency'].length > 0 ? frequencyTransfer[product['frequency']] : 'year'}.`,
        contactRate: `Average number of times a customer is contacting per ${product['frequency'].length > 0 ? frequencyTransfer[product['frequency']] : 'year'}.`,
        contactChange: `${positiveDriver ? 'Decrease' : 'Increase'} in customers contacting when using your product compared to the competitive alternative.`,
        contactDuration: 'The average time the employee type is spending on resolving an issue when contacted by a customer, e.g. time on the phone, time emailing, time working in internal systems etc.',
        efficiencyChange: `Efficiency ${positiveDriver ? 'gain' : 'loss'} in contact resolution time for the employee type when using your product compared to the competitive alternative.`,
        shareWithCompensation: 'Share of contacting customers that are getting some kind of compensation.',
        compensation: 'Compensation represents the average value of benefits—monetary or goods—provided to customers who receive such adjustments.'
    }

    const handleOutcomeChange = (event) => {
        formik.setFieldValue('outcome', event.target.value)
    };

    const handleOutcomeClose = () => {
        setOpenOutcome(false);
    };

    const handleOutcomeOpen = () => {
        setOpenOutcome(true);
    };

    useEffect(() => {
        formik.setFieldValue("outcome", outcomeList[0])
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [outcomeList]);

    useEffect(() => {
        if (editCSDriverId) {
            setPositiveDriver(editCSDriver["positive_driver"])

            formik.setFieldValue("multiple_type", editCSDriver["multiple_type"])
            formik.setFieldValue("cost_per_multiple", editCSDriver["cost_per_multiple"])
            formik.setFieldValue("min_per_contact", editCSDriver["min_per_contact"])
            formik.setFieldValue("efficiency_gain_contact", editCSDriver["efficiency_gain_contact"])
            formik.setFieldValue("share_contacting", editCSDriver["share_contacting"])
            formik.setFieldValue("contact_rate", editCSDriver["contact_rate"])
            formik.setFieldValue("contact_decrease", editCSDriver["contact_decrease"])
            formik.setFieldValue("share_with_compensation", editCSDriver["share_with_compensation"])
            formik.setFieldValue("compensation", formatNumberThsndAndDec(editCSDriver["compensation"]))
            formik.setFieldValue("positive_driver", editCSDriver["positive_driver"])
            formik.setFieldValue("outcome", editCSDriver["outcome"])
            formik.setFieldValue("driver_name", editCSDriver["driver_name"])
            formik.setFieldValue("comment", editCSDriver["comment"])
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editCSDriverId])

    useEffect(() => {
        formik.setFieldValue("driver_name", `${positiveDriver ? '' : 'Extra '}Customer Support${positiveDriver ? ' Savings' : ''}`)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [positiveDriver])

    const initialValues = {
        id: null,
        cost_type: 'customer_support',
        outcome: '',
        driver_name: `${positiveDriver ? '' : 'Extra '}Customer Support${positiveDriver ? ' Savings' : ''}`,
        comment: '',
        positive_driver: true,
        type: costStructure.type,
        currency: currency,
        multiple_type: costStructure.multiple_type,
        multiple: getMultipleInitialValues(costStructure),
        multiple_value: 0,
        cost_per_multiple: getSliderInitialValues(costStructure),
        min_per_contact: getMultipleInitialValues(costStructure),
        efficiency_gain_contact: getMultipleInitialValues(costStructure),
        share_contacting: 0,
        contact_rate: 1,
        contact_decrease: 0,
        share_with_compensation: 0,
        compensation: 0
    }

    const isNumeric = (value) => {
        return /^-?\d{1,3}(,\d{3})*(\.\d+)?$/.test(value);
    };

    const isPositive = (value) => {
        return parseFloat(value.toString().replace(/,/g, '')) >= 0;
    };

    const validate = (values) => {
        const errors = {
            efficiency_gain_contact: {},
            min_per_contact: {}
        };

        if (!values.driver_name) {
            errors.driver_name = 'Driver must have a name';
        }

        if (!isNumeric(values.share_contacting)) {
            errors.share_contacting = 'Must be numeric value';
        } else if (!isPositive(values.share_contacting)) {
            errors.share_contacting = 'Must be positive or zero';
        } else if (values.share_contacting > 100) {
            errors.share_contacting = 'Not more than 100';
        }

        if (!isNumeric(values.contact_rate)) {
            errors.contact_rate = 'Must be numeric value';
        } else if (!isPositive(values.contact_rate)) {
            errors.contact_rate = 'Must be positive or zero';
        }

        if (!isNumeric(values.contact_decrease)) {
            errors.contact_decrease = 'Must be numeric value';
        } else if (!isPositive(values.contact_decrease)) {
            errors.contact_decrease = 'Must be positive or zero';
        } else if (values.contact_decrease > 100) {
            errors.contact_decrease = 'Not more than 100';
        }

        if (!isNumeric(values.share_with_compensation)) {
            errors.share_with_compensation = 'Must be numeric value';
        } else if (!isPositive(values.share_with_compensation)) {
            errors.share_with_compensation = 'Must be positive or zero';
        } else if (values.share_with_compensation > 100) {
            errors.share_with_compensation = 'Not more than 100';
        }

        if (!isNumeric(values.compensation)) {
            errors.compensation = 'Must be numeric value';
        } else if (!isPositive(values.compensation)) {
            errors.compensation = 'Must be positive or zero';
        }

        costStructure.multiple_type.forEach(type => {
            if (!isNumeric(values.efficiency_gain_contact[type])) {
                errors.efficiency_gain_contact[type] = 'Must be numeric value';
            } else if (!isPositive(values.efficiency_gain_contact[type])) {
                errors.efficiency_gain_contact[type] = 'Must be positive or zero';
            } else if (values.efficiency_gain_contact[type] > 100) {
                errors.efficiency_gain_contact[type] = 'Not more than 100';
            }

            if (!isNumeric(values.min_per_contact[type])) {
                errors.min_per_contact[type] = 'Must be numeric value';
            } else if (!isPositive(values.min_per_contact[type])) {
                errors.min_per_contact[type] = 'Must be positive or zero';
            }

        })

        // Filter out empty nested error objects for efficiency_gain_contact and min_per_contact
        if (Object.keys(errors.efficiency_gain_contact).length === 0) {
            delete errors.efficiency_gain_contact;
        }
        if (Object.keys(errors.min_per_contact).length === 0) {
            delete errors.min_per_contact;
        }

        // If the errors object is empty (meaning no errors), return an empty object
        if (Object.keys(errors).length === 0) {
            return {};
        }

        return errors
    }

    const formik = useFormik({
        initialValues: initialValues,
        validate: validate,
        onSubmit: onSubmit,
    });

    useImperativeHandle(ref, () => ({
        resetForm() {
            formik.handleReset()
            setPositiveDriver(true)
        },
    }));

    return (
        <Box component="form" onSubmit={formik.handleSubmit} sx={{ ml: 2, maxWidth: 1200 }}>
            <Box sx={{ mt: 3, mb: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                    1. Number of Contacts
                </Typography>
            </Box>

            <Grid container columnSpacing={2} >
                <Grid item xs={2}>
                    <Typography variant="caption" sx={{ mr: 1 }}>
                        % of customers contacting{product['frequency'].length > 0 ? ` per ${frequencyTransfer[product['frequency']]}` : ''}{tooltip(tooltipText.shareContacting)}
                    </Typography>
                </Grid>
                <Grid item xs={2} sx={{ display: 'flex', alignItems: 'flex-end' }}>
                    <Typography variant="caption" sx={{ mr: 1 }}>
                        Contact rate per customer{product['frequency'].length > 0 ? ` per ${frequencyTransfer[product['frequency']]}` : ''}{tooltip(tooltipText.contactRate)}
                    </Typography>
                </Grid>
                <Grid item xs={2} sx={{ display: 'flex', alignItems: 'flex-end' }}>
                    <Typography variant="caption" sx={{ mr: 1 }}>
                        Contact {positiveDriver ? 'decrease' : 'increase'}{tooltip(tooltipText.contactChange)}
                    </Typography>
                </Grid>
                <Grid item xs={6} />
                <Grid item xs={2}>
                    <TextFieldCustom
                        name="share_contacting"
                        endAdornment="%"
                        value={formik.values.share_contacting}
                        onChange={(event) => {
                            const { value } = event.target
                            const formattedValue = formatNumberThsndAndDec(value)
                            formik.setFieldValue("share_contacting", formattedValue || '')
                        }}
                        onBlur={formik.handleBlur}
                        error={getIn(formik.errors, "share_contacting") && Boolean(getIn(formik.errors, "share_contacting"))}
                        helperText={getIn(formik.touched, "share_contacting") && getIn(formik.errors, "share_contacting")}
                    />
                </Grid>
                <Grid item xs={2}>
                    <TextFieldCustom
                        name="contact_rate"
                        endAdornment={
                            <Typography variant="caption">
                                contacts
                            </Typography>
                        }
                        value={formik.values.contact_rate}
                        onChange={(event) => {
                            const { value } = event.target
                            const formattedValue = formatNumberThsndAndDec(value)
                            formik.setFieldValue("contact_rate", formattedValue || '')
                        }}
                        onBlur={formik.handleBlur}
                        error={getIn(formik.errors, "contact_rate") && Boolean(getIn(formik.errors, "contact_rate"))}
                        helperText={getIn(formik.touched, "contact_rate") && getIn(formik.errors, "contact_rate")}
                    />
                </Grid>
                <Grid item xs={2}>
                    <TextFieldCustom
                        name="contact_decrease"
                        endAdornment="%"
                        value={formik.values.contact_decrease}
                        onChange={(event) => {
                            const { value } = event.target
                            const formattedValue = formatNumberThsndAndDec(value)
                            formik.setFieldValue("contact_decrease", formattedValue || '')
                        }}
                        onBlur={formik.handleBlur}
                        error={getIn(formik.errors, "contact_decrease") && Boolean(getIn(formik.errors, "contact_decrease"))}
                        helperText={getIn(formik.touched, "contact_decrease") && getIn(formik.errors, "contact_decrease")}
                        greenBackground={positiveDriver}
                        redBackground={!positiveDriver}
                    />
                </Grid>

            </Grid>

            <Box sx={{ mt: 3 }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                    2. Labor costs
                </Typography>
            </Box>

            {costStructure.multiple_type.map((type, index) => {
                return (
                    <Grid container columnSpacing={2} key={type}>
                        {index === 0 ?
                            <React.Fragment>
                                <Grid item xs={2} />
                                <Grid item xs={4}>
                                    <Typography variant="caption">
                                        {`Annual cost per ${costStructure.multiple}`}
                                    </Typography>
                                </Grid>
                                <Grid item xs={2}>
                                    <Typography variant="caption" sx={{ mr: 1 }}>
                                        Contact duration{tooltip(tooltipText.contactDuration)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={2}>
                                    <Typography variant="caption" sx={{ mr: 1 }}>
                                        Efficiency {positiveDriver ? 'gain' : 'loss'}{tooltip(tooltipText.efficiencyChange)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={2} />
                            </React.Fragment>
                            : null}
                        <Grid item xs={2}>
                            <Typography variant="body2" sx={{ mr: 1, mt: 1 }}>
                                {firstCharToUpper(type)}
                            </Typography>
                        </Grid>
                        <Grid item xs={4}>
                            <Slider
                                name={`cost_per_multiple.${type}`}
                                value={formik.values.cost_per_multiple[type]}
                                onChange={formik.handleChange}
                                valueLabelDisplay="auto"
                                marks={createSliderMarks(costStructure.cost_per_multiple[type])}
                                min={costStructure.cost_per_multiple[type].lower * 0}
                                max={costStructure.cost_per_multiple[type].higher * 1.2}
                                step={getMagnitude(costStructure.cost_per_multiple[type].lower) / 10}
                                valueLabelFormat={value => addCurrencyFrequency(formatNumberMagnitude(value), costStructure.cost_per_multiple[type].currency, null)}
                                sx={{ maxWidth: 300 }}
                            />
                        </Grid>
                        <Grid item xs={2}>
                            <TextFieldCustom
                                name={`min_per_contact.${type}`}
                                endAdornment={
                                    <Typography variant="caption">
                                        min
                                    </Typography>
                                }
                                value={formik.values.min_per_contact[type]}
                                onChange={(event) => {
                                    const { value } = event.target
                                    const formattedValue = formatNumberThsndAndDec(value)
                                    formik.setFieldValue(`min_per_contact.${type}`, formattedValue || '')
                                }}
                                onBlur={formik.handleBlur}
                                error={getIn(formik.errors, `min_per_contact.${type}`) && Boolean(getIn(formik.errors, `min_per_contact.${type}`))}
                                helperText={getIn(formik.touched, `min_per_contact.${type}`) && getIn(formik.errors, `min_per_contact.${type}`)}
                            />
                        </Grid>
                        <Grid item xs={2}>
                            <TextFieldCustom
                                name={`efficiency_gain_contact.${type}`}
                                endAdornment="%"
                                value={formik.values.efficiency_gain_contact[type]}
                                onChange={(event) => {
                                    const { value } = event.target
                                    const formattedValue = formatNumberThsndAndDec(value)
                                    formik.setFieldValue(`efficiency_gain_contact.${type}`, formattedValue || '')
                                }}
                                onBlur={formik.handleBlur}
                                error={getIn(formik.errors, `efficiency_gain_contact.${type}`) && Boolean(getIn(formik.errors, `efficiency_gain_contact.${type}`))}
                                helperText={getIn(formik.touched, `efficiency_gain_contact.${type}`) && getIn(formik.errors, `efficiency_gain_contact.${type}`)}
                                greenBackground={positiveDriver}
                                redBackground={!positiveDriver}
                            />
                        </Grid>
                    </Grid>
                )
            })

            }

            <Box sx={{ mt: 3, mb: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                    3. Compensation costs
                </Typography>
            </Box>
            <Grid container columnSpacing={2}>
                <Grid item xs={2}>
                    <Typography variant="caption" sx={{ mr: 1 }}>
                        % of contacts with compensation{tooltip(tooltipText.shareWithCompensation)}
                    </Typography>
                </Grid>
                <Grid item xs={2}>
                    <Typography variant="caption" sx={{ mr: 1 }}>
                        Compensation per contact e.g. monetary or goods{tooltip(tooltipText.compensation)}
                    </Typography>
                </Grid>
                <Grid item xs={12} />
                <Grid item xs={2}>
                    <TextFieldCustom
                        name="share_with_compensation"
                        endAdornment="%"
                        value={formik.values.share_with_compensation}
                        onChange={(event) => {
                            const { value } = event.target
                            const formattedValue = formatNumberThsndAndDec(value)
                            formik.setFieldValue("share_with_compensation", formattedValue || '')
                        }}
                        onBlur={formik.handleBlur}
                        error={getIn(formik.errors, "share_with_compensation") && Boolean(getIn(formik.errors, "share_with_compensation"))}
                        helperText={getIn(formik.touched, "share_with_compensation") && getIn(formik.errors, "share_with_compensation")}
                    />
                </Grid>
                <Grid item xs={2}>
                    <TextFieldCustom
                        name="compensation"
                        startAdornment={currency}
                        value={formik.values.compensation}
                        onChange={(event) => {
                            const { value } = event.target
                            const formattedValue = formatNumberThsndAndDec(value)
                            formik.setFieldValue("compensation", formattedValue || '')
                        }}
                        onBlur={formik.handleBlur}
                        error={getIn(formik.errors, "compensation") && Boolean(getIn(formik.errors, "compensation"))}
                        helperText={getIn(formik.touched, "compensation") && getIn(formik.errors, "compensation")}
                    />
                </Grid>
            </Grid>
            <Box sx={{ mt: 3, mb: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                    4. Driver details
                </Typography>
            </Box>
            <Grid container columnSpacing={2} sx={{ mt: 3 }}>
                <Grid item xs={2}>
                    <Stack direction="column" gap={1}>
                        <Typography variant="caption" sx={{ mr: 1 }}>
                            Value driver{tooltip(generalTooltipText.differentiationValue)}
                        </Typography>
                        <GreenRedCompactSwitch
                            name="positive_driver"
                            leftLabel="Negative"
                            rightLabel="Positive"
                            value={formik.values.positive_driver}
                            onChange={(event) => {
                                formik.handleChange(event)
                                setPositiveDriver(!positiveDriver)
                            }}
                        />

                    </Stack>
                </Grid>
            </Grid>
            {!outcome &&
                <Stack direction="column" gap={1} sx={{ mt: 2 }}>
                    <Typography variant="caption">
                        Outcome
                    </Typography>
                    <Box>
                        <Select
                            open={openOutcome}
                            onClose={handleOutcomeClose}
                            onOpen={handleOutcomeOpen}
                            value={formik.values.outcome}
                            onChange={handleOutcomeChange}
                            size='small'
                        >
                            {outcomeList.map((outcome) => (
                                <MenuItem key={outcome} value={outcome}>
                                    <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                        {outcome}
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>
                </Stack>
            }
            <Grid container columnSpacing={2} sx={{ mt: 3 }}>
                <Grid item xs={3}>
                    <Typography variant="caption" sx={{ mr: 1 }}>
                        Name of driver
                    </Typography>
                </Grid>
                <Grid item xs={5}>
                    <Typography variant="caption" sx={{ mr: 1 }}>
                        Comment
                    </Typography>
                </Grid>
                <Grid item xs={3} />
                <Grid item xs={3}>
                    <TextFieldCustom
                        name="driver_name"
                        value={formik.values.driver_name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={getIn(formik.errors, "driver_name") && Boolean(getIn(formik.errors, "driver_name"))}
                        helperText={
                            (getIn(formik.touched, "driver_name") && getIn(formik.errors, "driver_name")) ||
                                (getIn(formik.errors, "driver_name") && getIn(formik.errors, "driver_name").includes("already"))
                                ? getIn(formik.errors, "driver_name")
                                : ''
                        }
                        fullWidth
                    />
                </Grid>
                <Grid item xs={5}>
                    <TextFieldCustom
                        name="comment"
                        value={formik.values.comment}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={getIn(formik.errors, "comment") && Boolean(getIn(formik.errors, "comment"))}
                        helperText={getIn(formik.touched, "comment") && getIn(formik.errors, "comment")}
                        fullWidth
                    />
                </Grid>
            </Grid>


            <Box sx={{ mt: 3, mb: 4 }}>
                {formActions()}
            </Box>
        </Box>
    )

})

export default CostValueDriverCSForm